/**
 * Composable Report Widget shell.
 * Optional visualization + optional table; single dataset with limit/load-more driving both.
 */
import { Box, SxProps, Theme, Typography } from '@mui/material';
import { memo, useMemo } from 'react';

import { getChartColors } from './chartColors';
import { ComposableWidgetSideBySide } from './layout/ComposableWidgetSideBySide';
import { ComposableWidgetStacked } from './layout/ComposableWidgetStacked';

import { useComposableWidgetInteractions } from '../model/hooks/useComposableWidgetInteractions';
import { useComposableWidgetState } from '../model/hooks/useComposableWidgetState';

import type {
    ReportWidgetConfig,
    ReportWidgetItem,
    ReportWidgetTableConfig,
} from '../model/types';

interface ComposableReportWidgetProps {
    config: ReportWidgetConfig;
    items: ReportWidgetItem[];
    /** When provided, chart uses this instead of (limited) items. Use for e.g. pie with first N + "Other". */
    chartItems?: ReportWidgetItem[];
    loading?: boolean;
    disableChartAnimation?: boolean;
    widgetSx?: SxProps<Theme>;
}

function titleToTestClass(title: string): string {
    return (
        title
            .trim()
            .toLowerCase()
            .replaceAll(/[^a-z0-9]+/g, '-')
            .replaceAll(/^-|-$/g, '') || 'composable-report-widget'
    );
}

export const ComposableReportWidget = memo(
    ({
        config,
        items,
        chartItems: chartItemsOverride,
        loading = false,
        disableChartAnimation = false,
        widgetSx = {},
    }: ComposableReportWidgetProps) => {
        const {
            widgetId,
            title,
            description,
            visualization,
            table: tableConfig,
            pagination,
            onRowClick,
            onChartItemClick,
            onDetailsClick,
        } = config;

        const table: ReportWidgetTableConfig = tableConfig ?? {
            enabled: false,
            columns: [],
        };

        const layout = config.layout ?? 'stacked';
        const isSideBySide = layout === 'sideBySide';

        const {
            visibleItems,
            isInitialLoading,
            isLoadingMore,
            hasMore,
            loadMoreCount,
            handleLoadMore,
            canCollapse,
            handleCollapse,
        } = useComposableWidgetState({
            items,
            pagination,
            loading,
        });

        const chartItems =
            chartItemsOverride ?? (table.enabled ? visibleItems : items);

        // Chart colors
        const paletteColors = useMemo(
            () =>
                getChartColors({
                    palette: visualization.options?.palette,
                    colors: visualization.options?.colors,
                }),
            [visualization.options?.palette, visualization.options?.colors],
        );

        const itemColors = useMemo(
            () =>
                visibleItems.map(
                    (item, i) =>
                        item.color ?? paletteColors[i % paletteColors.length],
                ),
            [visibleItems, paletteColors],
        );

        const {
            hoveredItemId,
            tableActiveItemId,
            handleRowClick,
            handleChartItemClick,
            handleDetailsClick,
            handleChartItemHover,
            handleTableRowHover,
            handleLoadMoreHover,
        } = useComposableWidgetInteractions({
            widgetId,
            chartItems,
            isSideBySide,
            onRowClick,
            onChartItemClick,
            onDetailsClick,
        });

        return (
            <Box
                data-testclass={
                    title
                        ? titleToTestClass(`report-${title}`)
                        : `composable-report-widget--${widgetId}`
                }
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: config.hasTopLegend ? '10px' : '18px',
                    overflow: 'visible',
                    boxSizing: 'border-box',
                    ...widgetSx,
                }}
            >
                {title && (
                    <Box sx={{ marginBottom: description ? 0.5 : 0 }}>
                        <Typography
                            variant="h6"
                            component="h2"
                            sx={{ color: 'common.black' }}
                        >
                            {title}
                        </Typography>
                        {description && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    marginTop: 0.5,
                                }}
                            >
                                {description}
                            </Typography>
                        )}
                    </Box>
                )}

                {isSideBySide && visualization.enabled && table.enabled ? (
                    <ComposableWidgetSideBySide
                        chartItems={chartItems}
                        visibleItems={visibleItems}
                        visualization={visualization}
                        table={table}
                        loading={loading}
                        disableChartAnimation={disableChartAnimation}
                        isInitialLoading={isInitialLoading}
                        isLoadingMore={isLoadingMore}
                        hasMore={hasMore}
                        handleLoadMore={handleLoadMore}
                        loadMoreCount={loadMoreCount}
                        canCollapse={canCollapse}
                        handleCollapse={handleCollapse}
                        itemColors={itemColors}
                        rowClickHandler={handleRowClick}
                        detailsClickHandler={handleDetailsClick}
                        chartItemClickHandler={handleChartItemClick}
                        hoveredItemId={hoveredItemId}
                        tableActiveItemId={tableActiveItemId}
                        chartHoverHandler={handleChartItemHover}
                        tableRowHoverHandler={handleTableRowHover}
                        loadMoreHoverHandler={handleLoadMoreHover}
                    />
                ) : (
                    <ComposableWidgetStacked
                        chartItems={chartItems}
                        visibleItems={visibleItems}
                        visualization={visualization}
                        table={table}
                        showChart={visualization.enabled}
                        loading={loading}
                        disableChartAnimation={disableChartAnimation}
                        isInitialLoading={isInitialLoading}
                        isLoadingMore={isLoadingMore}
                        hasMore={hasMore}
                        handleLoadMore={handleLoadMore}
                        loadMoreCount={loadMoreCount}
                        canCollapse={canCollapse}
                        handleCollapse={handleCollapse}
                        itemColors={itemColors}
                        rowClickHandler={handleRowClick}
                        detailsClickHandler={handleDetailsClick}
                        chartItemClickHandler={handleChartItemClick}
                        hoveredItemId={hoveredItemId}
                        tableActiveItemId={tableActiveItemId}
                        chartHoverHandler={handleChartItemHover}
                    />
                )}
            </Box>
        );
    },
);

ComposableReportWidget.displayName = 'ComposableReportWidget';
